import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  FileText,
  Plus,
  Save,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  X,
  Loader2,
  Menu as MenuIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const EMPTY = {
  slug: "",
  title: "",
  content: "",
  menu_location: "none",
  menu_label: "",
  order: 0,
  published: true,
};

const LOCATION_TONE = {
  header: "bg-primary/10 text-primary",
  footer: "bg-secondary text-secondary-foreground",
  none: "bg-muted text-muted-foreground",
};

export default function Pages() {
  const [pages, setPages] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null | {...page} | EMPTY

  const load = async () => {
    try {
      const { data } = await api.get("/pages");
      setPages(data);
    } catch (e) {
      toast.error("Failed to load pages");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing._isNew) {
        delete payload._isNew;
        await api.post("/pages", payload);
        toast.success("Page created");
      } else {
        await api.put(`/pages/${editing._originalSlug}`, payload);
        toast.success("Page updated");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (slug) => {
    if (!confirm(`Delete page "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/pages/${slug}`);
      toast.success("Page deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const groups = useMemo(() => {
    const g = { header: [], footer: [], none: [] };
    (pages || []).forEach((p) => (g[p.menu_location] || g.none).push(p));
    return g;
  }, [pages]);

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            Content
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Pages
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Create custom pages (About, Privacy, Terms, FAQ…) and pin them to the
            header menu or footer. Content supports plain text or basic HTML.
          </p>
        </div>
        <Button
          data-testid="pages-new-btn"
          onClick={() => setEditing({ ...EMPTY, _isNew: true })}
          className="shrink-0"
        >
          <Plus className="w-4 h-4 mr-2" /> New page
        </Button>
      </div>

      {pages === null ? (
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : pages.length === 0 ? (
        <div
          data-testid="pages-empty"
          className="bg-card border border-dashed border-border rounded-2xl p-10 text-center"
        >
          <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
          <div className="text-base font-bold mb-1">No pages yet</div>
          <div className="text-sm text-muted-foreground mb-4">
            Create your first page. Try an "About" page pinned to the header.
          </div>
          <Button
            data-testid="pages-empty-new-btn"
            onClick={() => setEditing({ ...EMPTY, _isNew: true })}
          >
            <Plus className="w-4 h-4 mr-2" /> Create page
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          <Group label="Header menu" tone="header" items={groups.header} onEdit={setEditing} onDelete={del} />
          <Group label="Footer links" tone="footer" items={groups.footer} onEdit={setEditing} onDelete={del} />
          <Group label="Unpinned" tone="none" items={groups.none} onEdit={setEditing} onDelete={del} />
        </div>
      )}

      {editing && (
        <div
          role="dialog"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-card w-full max-w-2xl rounded-2xl border border-border shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  {editing._isNew ? "New page" : "Edit page"}
                </div>
                <div className="text-lg font-bold">
                  {editing.title || "Untitled"}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEditing(null)}
                data-testid="pages-close-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    Title
                  </Label>
                  <Input
                    data-testid="pages-input-title"
                    value={editing.title}
                    onChange={(e) => {
                      const t = e.target.value;
                      setEditing((p) => ({
                        ...p,
                        title: t,
                        // Auto-populate slug on new pages, leave alone on edits.
                        slug:
                          p._isNew && (!p.slug || p.slug === autoSlug(p.title))
                            ? autoSlug(t)
                            : p.slug,
                      }));
                    }}
                    placeholder="About NearMe"
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    URL slug
                  </Label>
                  <Input
                    data-testid="pages-input-slug"
                    value={editing.slug}
                    onChange={(e) =>
                      setEditing((p) => ({ ...p, slug: autoSlug(e.target.value) }))
                    }
                    placeholder="about"
                    className="mt-1.5 h-11 mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Public URL: <span className="mono">/{editing.slug || "…"}</span>
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Content (plain text or basic HTML)
                </Label>
                <Textarea
                  data-testid="pages-input-content"
                  value={editing.content}
                  onChange={(e) =>
                    setEditing((p) => ({ ...p, content: e.target.value }))
                  }
                  placeholder="<h2>About us</h2><p>NearMe is…</p>"
                  className="mt-1.5 min-h-[220px] mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Tags <span className="mono">h1–h6, p, ul, ol, li, a, strong, em, br, hr</span> are rendered as HTML.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    Menu location
                  </Label>
                  <Select
                    value={editing.menu_location}
                    onValueChange={(v) =>
                      setEditing((p) => ({ ...p, menu_location: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid="pages-select-location"
                      className="mt-1.5 h-11"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unpinned</SelectItem>
                      <SelectItem value="header">Header menu</SelectItem>
                      <SelectItem value="footer">Footer links</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    Menu label (optional)
                  </Label>
                  <Input
                    data-testid="pages-input-menu-label"
                    value={editing.menu_label || ""}
                    onChange={(e) =>
                      setEditing((p) => ({ ...p, menu_label: e.target.value }))
                    }
                    placeholder="Uses title if blank"
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    Order
                  </Label>
                  <Input
                    data-testid="pages-input-order"
                    type="number"
                    value={editing.order}
                    onChange={(e) =>
                      setEditing((p) => ({ ...p, order: e.target.value }))
                    }
                    className="mt-1.5 h-11"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Switch
                  data-testid="pages-switch-published"
                  checked={editing.published}
                  onCheckedChange={(v) =>
                    setEditing((p) => ({ ...p, published: v }))
                  }
                />
                <div>
                  <div className="text-sm font-semibold">
                    {editing.published ? "Published" : "Draft"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {editing.published
                      ? "Publicly visible at the URL above."
                      : "Hidden from public. Menu link won't appear either."}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-border flex items-center justify-between sticky bottom-0 bg-card">
              {editing.slug && !editing._isNew ? (
                <a
                  href={`/${editing.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                >
                  Open public page <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={save}
                  disabled={saving}
                  data-testid="pages-save-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const autoSlug = (s = "") =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const Group = ({ label, tone, items, onEdit, onDelete }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <MenuIcon className="w-4 h-4 text-muted-foreground" />
      <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
        {label}
      </div>
      <span className="text-xs font-semibold text-muted-foreground">
        · {items.length}
      </span>
    </div>
    {items.length === 0 ? (
      <div className="text-xs text-muted-foreground italic px-1">
        No pages in this group.
      </div>
    ) : (
      <div className="space-y-2">
        {items
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((p) => (
            <div
              key={p.slug}
              data-testid={`page-row-${p.slug}`}
              className="bg-card border border-border rounded-xl p-4 flex items-center gap-4 hover:shadow-sm transition-shadow"
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                  LOCATION_TONE[p.menu_location] || ""
                }`}
              >
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-bold truncate">{p.title}</div>
                  {!p.published && (
                    <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                      Draft
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mono">
                  /{p.slug} · order {p.order ?? 0}
                </div>
              </div>
              <a
                href={`/${p.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground"
                title="Open"
              >
                {p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </a>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  onEdit({
                    ...p,
                    _originalSlug: p.slug,
                    menu_label: p.menu_label || "",
                  })
                }
                data-testid={`page-edit-${p.slug}`}
              >
                Edit
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onDelete(p.slug)}
                data-testid={`page-delete-${p.slug}`}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
      </div>
    )}
  </div>
);
